import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "card-global" }
const _hoisted_2 = { class: "flex" }
const _hoisted_3 = { class: "page-title" }
const _hoisted_4 = { class: "filter-container" }
const _hoisted_5 = { class: "grid grid-cols-12 gap-6" }
const _hoisted_6 = { class: "col-span-4 md:col-span-4 sm:col-span-4" }
const _hoisted_7 = {
  for: "company-name",
  class: "block text-sm font-medium text-gray-700"
}
const _hoisted_8 = { class: "col-span-4 md:col-span-4 sm:col-span-4" }
const _hoisted_9 = { class: "block text-sm" }
const _hoisted_10 = { value: null }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "mt-10 flex justify-between" }
const _hoisted_13 = {
  key: 0,
  class: "inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800"
}
const _hoisted_14 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_15 = { class: "relative" }
const _hoisted_16 = { class: "flex flex-row-reverse" }

import { ref, onMounted, computed, reactive } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { DocumentConstructorActions } from "@/store/modules/documentConstructor/actions";
import { DocumentConstructorGetters } from "@/store/modules/documentConstructor/getters";
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/vue/solid";
import DocumentConstructorListItem from "@/types/documents/documentConstructor/DocumentConstructorListItem";
import DeactivateEntityModal from "@/components/common/DeactivateEntityModal.vue";
import {
  PlusIcon,
  SearchIcon,
} from "@heroicons/vue/outline";
import VueTableLite from "vue3-table-lite/ts";
import StatusLabel from "@/components/common/StatusLabel.vue";
import TablePagination from "@/components/common/pagination/TablePagination.vue";
import { ActionMenu } from "@/components/common";
import { Button } from "@/components/common";
import { t } from "@/i18n";
import { AuthGetters } from "@/store/modules/auth";
import { getCommonStatusOptions } from "@/utils/labels";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const store = useStore();
const router = useRouter();
const toast = useToast();

const deactivateModalShow = ref(false);
const deactivateTargetId = ref<string | null>(null);

const dataProvider = computed(() => {
  return store.getters[DocumentConstructorGetters.GET_DATA_PROVIDER];
});
const documentConstructors = computed(() => {
  return dataProvider.value.items;
});
const pager = computed(() => {
  return dataProvider.value.pager;
});
const currentUser = computed(() => {
  return store.getters[AuthGetters.GET_USER_IDENTITY];
});
const statusOptions = computed(() => getCommonStatusOptions());
const canCreateDocumentConstructor = computed(() => {
  const user = currentUser.value;
  if (!user) {
    return false;
  }
  if (user.systemConfig?.documentConstructor === false) {
    return false;
  }
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    return (
      user.permissions.includes("DOCUMENT_CONSTRUCTOR_CREATE") ||
      user.systemConfig?.documentConstructor === true
    );
  }
  return user.systemConfig?.documentConstructor !== false;
});

const table = reactive({
  isLoading: false,
  columns: [
    {
      label: t("common.title"),
      field: "name",
      sortable: false,
    },
    {
      label: t("common.status"),
      field: "status",
      sortable: false,
    },
    {
      label: t("entityApproval.title"),
      field: "approval",
      sortable: false,
    },
    {
      label: "",
      field: "action",
      sortable: false,
    },
  ],
  rows: documentConstructors,
  totalRecordCount: 0,
});

const getDropDownActions = (item: DocumentConstructorListItem) => {
  const actions = [
    {
      text: t("common.details"),
      icon: EyeIcon,
      iconColor: "text-slate-500",
      action: (id: string) => {
        router.push({
          name: "DocumentConstructorDetails",
          params: { id },
        });
      },
    },
  ];

  if (item.hasPendingApproval && item.pendingApprovalId) {
    actions.push({
      text: t("approval.viewRequest"),
      icon: EyeIcon,
      iconColor: "text-yellow-600",
      action: () => {
        router.push({
          name: "EntityApprovalDetails",
          params: { id: item.pendingApprovalId },
        });
      },
    });
  }

  if (item.canEdit !== false) {
    actions.push({
      text: t("common.edit"),
      icon: PencilIcon,
      iconColor: "text-green-500",
      action: (id: string) => {
        router.push({ name: "DocumentConstructorEdit", params: { id } });
      },
    });
  }

  if (item.canDeactivate !== false) {
    actions.push({
      text: t("userDetails.delete"),
      icon: TrashIcon,
      iconColor: "text-red-500",
      action: (id: string) => {
        deactivateTargetId.value = id;
        deactivateModalShow.value = true;
      },
    });
  }

  return actions;
};

const confirmDeactivate = async (coordinatorId?: string) => {
  if (deactivateTargetId.value == null) {
    return;
  }
  try {
    await store.dispatch(DocumentConstructorActions.DELETE, {
      id: deactivateTargetId.value,
      coordinatorId,
    });
    toast.success(t("approval.submitted"));
    deactivateModalShow.value = false;
    await store.dispatch(DocumentConstructorActions.FETCH_DATA_PROVIDER, {
      page: pager.value.currentPage,
      ...filter.value,
    });
  } catch {
    toast.error(t("deactivate.error"));
  }
};

const getPage = async (page: number) => {
  await store.dispatch(DocumentConstructorActions.FETCH_DATA_PROVIDER, {
    page,
    ...filter.value,
  });
};

// Filter
const filter = ref({
  name: "",
  status: null,
});
const updateFilter = async () => {
  try {
    await store.dispatch(
      DocumentConstructorActions.FETCH_DATA_PROVIDER,
      { page: 1, ...filter.value }
    );
  } catch (e) {
    console.log(e);
  }
};

onMounted(async () => {
  await store.dispatch(DocumentConstructorActions.FETCH_DATA_PROVIDER, {
    page: 1,
  });
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)("docConstructorList.title")), 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("label", _hoisted_7, _toDisplayString(_unref(t)("common.name")), 1),
            _withDirectives(_createElementVNode("input", {
              id: "company-name",
              type: "search",
              class: "w-full flex-none w-2/5 rounded-lg border-gray-300",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter.value.name) = $event))
            }, null, 512), [
              [_vModelText, filter.value.name]
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, _toDisplayString(_unref(t)("common.status")), 1),
            _withDirectives(_createElementVNode("select", {
              id: "user-status",
              name: "user-status",
              class: "block w-full flex-1 rounded-lg border-gray-300",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filter.value.status) = $event))
            }, [
              _createElementVNode("option", _hoisted_10, _toDisplayString(_unref(t)("common.allStatuses")), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(statusOptions), (status, index) => {
                return (_openBlock(), _createElementBlock("option", {
                  value: index,
                  key: index
                }, _toDisplayString(status), 9, _hoisted_11))
              }), 128))
            ], 512), [
              [_vModelSelect, filter.value.status]
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(_unref(Button), {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (updateFilter())),
            class: "btn-search"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(SearchIcon), { class: "button-icon mr-2" }),
              _createTextVNode(" " + _toDisplayString(_unref(t)("common.search")), 1)
            ]),
            _: 1
          }),
          (_unref(canCreateDocumentConstructor))
            ? (_openBlock(), _createBlock(_component_router_link, {
                key: 0,
                to: { name: 'DocumentConstructorCreate' }
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), { class: "flex px-10" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(PlusIcon), { class: "button-icon mr-2" }),
                      _createTextVNode(" " + _toDisplayString(_unref(t)("common.create")), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _createVNode(_unref(VueTableLite), {
        "data-pagination-scroll-anchor": "",
        "is-slot-mode": true,
        "is-loading": _unref(table).isLoading,
        columns: _unref(table).columns,
        rows: _unref(table).rows,
        total: _unref(table).totalRecordCount,
        sortable: false,
        "is-hide-paging": true,
        messages: { noDataAvailable: _unref(t)('common.noData') }
      }, {
        status: _withCtx((data) => [
          _createVNode(StatusLabel, {
            label: data.value.status
          }, null, 8, ["label"])
        ]),
        approval: _withCtx((data) => [
          (data.value.hasPendingApproval)
            ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                _createTextVNode(_toDisplayString(_unref(t)("approval.pending")) + " ", 1),
                (data.value.pendingActionType)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(" (" + _toDisplayString(data.value.pendingActionType) + ") ", 1)
                    ], 64))
                  : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createElementBlock("span", _hoisted_14, "—"))
        ]),
        action: _withCtx((data) => [
          _createElementVNode("div", _hoisted_15, [
            _createVNode(_unref(ActionMenu), {
              actions: getDropDownActions(data.value),
              item: data.value
            }, null, 8, ["actions", "item"])
          ])
        ]),
        _: 1
      }, 8, ["is-loading", "columns", "rows", "total", "messages"]),
      _createElementVNode("div", _hoisted_16, [
        _createVNode(TablePagination, {
          current: _unref(pager).currentPage,
          "onUpdate:current": _cache[3] || (_cache[3] = ($event: any) => ((_unref(pager).currentPage) = $event)),
          "total-pages": _unref(pager).totalPages,
          "onUpdate:total-pages": _cache[4] || (_cache[4] = ($event: any) => ((_unref(pager).totalPages) = $event)),
          "start-page": _unref(pager).startPage,
          "end-page": _unref(pager).endPage,
          onPageChanged: getPage
        }, null, 8, ["current", "total-pages", "start-page", "end-page"])
      ])
    ]),
    _createVNode(DeactivateEntityModal, {
      show: deactivateModalShow.value,
      onHide: _cache[5] || (_cache[5] = ($event: any) => (deactivateModalShow.value = false)),
      onConfirm: confirmDeactivate
    }, null, 8, ["show"])
  ], 64))
}
}

})